import { React, useState, useEffect } from 'react';
import getBackend from 'backend/backend';
import { useTranslation } from "react-i18next";
import { Form, Button, Container, Alert, ProgressBar } from 'react-bootstrap';
import SidebarPage from 'components/sidebar_page';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { useNavigate } from 'react-router-dom';
import { Paths } from 'paths';

const ChatDataIndex = () => {
    const [t] = useTranslation('common');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [models, setModels] = useState([]);
    const [model, setModel] = useState("");
    const [text, setText] = useState("");
    const [settings, setSettings] = useState({});
    const [messages, setMessages] = useState([{
        role: "system",
        content: "You are a helpful assistant."
    }]);

    const [reqPending, setReqPending] = useState(false);
    const [dataIndexes, setDataIndexes] = useState([]);
    const [dataIndexId, setDataIndexId] = useState("");

    const navigate = useNavigate();

    useEffect(() => {
        let canceled = false;

        const fetchSettings = async () => {
            try {
                const result = await getBackend().getSettings();
                if (!canceled && result.error == null) {
                    setSettings(result.response.settings);
                } else if (!canceled) {
                    setError(result.error);
                }
            } catch (err) {
                if (!canceled) setError(err.message);
            } finally {
                if (!canceled) setLoading(false);
            }
        };

        const fetchWorkers = async () => {
            try {
                const result = await getBackend().getWorkers();
                if (!canceled && result.error == null) {
                    const runnableWorkers = result.response.workers.filter(worker => worker.state === "running");
                    const uniqueModels = [...new Set(runnableWorkers.map(worker => worker.model))];
                    setModels(uniqueModels);
                    if (uniqueModels.length > 0) {
                        setModel(uniqueModels[0]);
                    }
                } else if (!canceled) {
                    setError(result.error);
                }
            } catch (err) {
                if (!canceled) setError(err.message);
            } finally {
                if (!canceled) setLoading(false);
            }
        };

        const fetchDataIndexes = async () => {
            const result = await getBackend().getDataIndexes();
            if (result.error !== null) {
                setError(result.error);
            } else {
                let dataIndexes = [];
                for (const dataIndex of result.response.data_indexes) {
                    if (dataIndex.state === "indexed") {
                        dataIndexes.push({
                            id: dataIndex.id,
                            fileSystemPath: dataIndex.file_system_path
                        });
                    }
                }
                if (dataIndexes.length > 0) {
                    setDataIndexId(dataIndexes[0].id);
                }
                setDataIndexes(dataIndexes);
            }
        }

        fetchWorkers();
        fetchSettings();
        fetchDataIndexes();

        return () => {
            canceled = true;
        };
    }, []);

    const handleSend = async () => {
        setError(null);

        if (text === "") {
            setError(t('Message is empty'));
            return;
        }

        if (model === "") {
            setError(t('Model is not set'));
            setText("");
            return;
        }

        if (dataIndexId === "") {
            setError(t('Data index is not set'));
            setText("");
            return;
        }

        if (settings.api_token === "") {
            setError(t('API token is not set'));
            setText("");
            return;
        }

        let message = {role: "user", content: text};
        setText("");

        let localMessages = [...messages, message];
        setMessages(localMessages);
        setReqPending(true);
        const result = await getBackend().chatDataIndexCompletions(settings.api_token, model, dataIndexId, localMessages);
        if (result.error == null) {
            setMessages([...localMessages, {role: "assistant", content: result.response.choices[0].message.content}]);
        } else {
            setError(result.error);
        }
        setReqPending(false);
    };

    return (
        <SidebarPage title={t('Chat with Data')} description={t('Chat with Data')}>
            <Container className="my-5">

                <h2 className="mb-4">{t('Chat with Data')}</h2>
                {loading &&
                    <div>{t('Loading...')}</div>
                }
                {error &&
                    <Alert variant="danger">{t('Error')}: {error}</Alert>
                }
                {models.length === 0 &&
                    <div>
                        <h3>{t('No running models available')}</h3>
                        <Button className="custom-button" variant="light" onClick={() => {
                            navigate(Paths.AddWorker.path);
                        }}>
                            {t('Run a model')}
                        </Button>   
                    </div>
                }
                {dataIndexes.length === 0 &&
                    <div>
                        <h3>{t('No data indexes available')}</h3>
                        <Button className="custom-button" variant="light" onClick={() => {
                            navigate(Paths.CreateDataIndex.path);
                        }}>
                            {t('Create a data index')}
                        </Button>   
                    </div>
                }
                {models.length > 0 && dataIndexes.length > 0 &&
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>{t('Select Model')}</Form.Label>
                        <Form.Select value={model} onChange={(e) => setModel(e.target.value)}>
                            {models.map((model) => (
                                <option key={model} value={model}>{model}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>{t('Select Data Index')}</Form.Label>
                        <Form.Select value={dataIndexId} onChange={(e) => setDataIndexId(e.target.value)}>
                            {dataIndexes.map((dataIndex) => (
                                <option key={dataIndex.id} value={dataIndex.id}>{dataIndex.id} - {dataIndex.fileSystemPath}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        {reqPending && (
                            <ProgressBar 
                                animated 
                                now={100} 
                                variant="primary"
                            />
                        )}
                    </Form.Group>

                    <div className="chat-messages mb-3" style={{height: "400px", overflowY: "auto", border: "1px solid #dee2e6", borderRadius: "0.25rem", padding: "1rem"}}>
                        {messages.map((message, index) => (
                            <div key={index} className={`message ${message.role}`}>
                                <small className="text-muted">{message.role}</small>
                                <div className="message-content">
                                    <ReactMarkdown
                                        remarkPlugins={[remarkMath]}
                                        rehypePlugins={[rehypeKatex]}
                                        components={{
                                            p: ({node, ...props}) => <p style={{marginBottom: '0.5rem'}} {...props}/>,
                                        }}
                                        children={typeof message.content === 'string'
                                            ? message.content
                                            : message.content.find(item => item.type === 'text')?.text || ''}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>

                    <Form.Group className="mb-3">
                        <Form.Control
                            as="textarea" 
                            rows={3}
                            placeholder={t('Type your message here...')}
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                        />
                    </Form.Group>

                    <Button className="custom-button" variant="light" onClick={handleSend}>
                        {t('Send')}
                    </Button>
                    <Button variant="secondary" className="ms-2" onClick={() => {
                        setError(null);
                        setMessages([{
                            role: "system",
                            content: "You are a helpful assistant."
                        }]);
                        setText("");
                    }}>
                        {t('Reset')}
                        </Button>
                    </Form>
                }
            </Container>
        </SidebarPage>
    );
};

export default ChatDataIndex;
